import { LitElement, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { timeAgo } from '../../base/package/util/time';
import style from './ui-time-ago.css?inline';

const CName = 'ui-time-ago';

// --- Shared timer registry ---
// One interval per tick rate, shared across all instances. Eliminates
// hundreds of per-instance setIntervals on pages with many game tiles.

const ONE_SECOND = 1_000;
const ONE_MINUTE = 60_000;
const ONE_HOUR = 3_600_000;

const secondInstances = new Set<UITimeAgo>();
const minuteInstances = new Set<UITimeAgo>();

let secondTimerId: number | null = null;
let minuteTimerId: number | null = null;

function tickSecond() {
  for (const instance of secondInstances) {
    instance.tick();
  }
}

function tickMinute() {
  for (const instance of minuteInstances) {
    instance.tick();
  }
}

function startSecondTimer() {
  if (secondTimerId === null && secondInstances.size > 0) {
    secondTimerId = window.setInterval(tickSecond, ONE_SECOND);
  }
}

function stopSecondTimer() {
  if (secondTimerId !== null && secondInstances.size === 0) {
    clearInterval(secondTimerId);
    secondTimerId = null;
  }
}

function startMinuteTimer() {
  if (minuteTimerId === null && minuteInstances.size > 0) {
    minuteTimerId = window.setInterval(tickMinute, ONE_MINUTE);
  }
}

function stopMinuteTimer() {
  if (minuteTimerId !== null && minuteInstances.size === 0) {
    clearInterval(minuteTimerId);
    minuteTimerId = null;
  }
}

function register(instance: UITimeAgo) {
  const age = instance.getAge();

  if (age >= ONE_HOUR) {
    // No updates needed
    return;
  }

  if (age >= ONE_MINUTE) {
    minuteInstances.add(instance);
    startMinuteTimer();
  } else {
    secondInstances.add(instance);
    startSecondTimer();
  }
}

function unregister(instance: UITimeAgo) {
  secondInstances.delete(instance);
  minuteInstances.delete(instance);
  stopSecondTimer();
  stopMinuteTimer();
}

function promoteToMinuteTier(instance: UITimeAgo) {
  secondInstances.delete(instance);
  stopSecondTimer();
  minuteInstances.add(instance);
  startMinuteTimer();
}

// --- Component ---

@customElement(CName)
export class UITimeAgo extends LitElement {
  static readonly styles = unsafeCSS(style);
  @property({ type: String }) timestamp: string;
  @property({ type: String }) locale?: 'de-DE' | 'en-US';
  @property({ type: Boolean }) displayAgoText = true;

  @state() private _timeAgo: string;

  /** Called by the shared timer on each tick. */
  public tick() {
    this._updateTimeAgo();
    const age = this.getAge();

    if (age >= ONE_HOUR) {
      // Stop updating — unregister from all timers
      unregister(this);
    } else if (age >= ONE_MINUTE && secondInstances.has(this)) {
      // Promote from 1s tier to 60s tier
      promoteToMinuteTier(this);
    }
  }

  /** Returns milliseconds elapsed since the timestamp. */
  public getAge(): number {
    return Date.now() - Math.trunc(Number(this.timestamp));
  }

  connectedCallback(): void {
    super.connectedCallback();
    if (this.timestamp) {
      this._updateTimeAgo();
      register(this);
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    unregister(this);
  }

  render() {
    return this._timeAgo;
  }

  private _updateTimeAgo() {
    this._timeAgo = timeAgo(this.timestamp, this.locale ?? 'en-US', this.displayAgoText);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UITimeAgo;
  }
}
